import { BAOCustomElement } from '@bao/custom-element'
import { Cart } from '@bao/cart'
import { sectionRenderer } from '@bao/section-rendering'
import { formatMoney, getElement } from '@bao/utils'

const KONVA_SRC = 'https://unpkg.com/konva@9.3.0/konva.min.js'

let konvaPromise = null

function loadKonva () {
  if (window.Konva) {
    return Promise.resolve(window.Konva)
  }

  if (konvaPromise) {
    return konvaPromise
  }

  konvaPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')

    script.src = KONVA_SRC
    script.async = true
    script.dataset.productPersonalisationKonva = ''

    script.addEventListener('load', () => {
      if (!window.Konva) {
        reject(new Error('Konva loaded but window.Konva is unavailable'))

        return
      }

      resolve(window.Konva)
    })

    script.addEventListener('error', () => {
      reject(new Error('Failed to load Konva'))
    })

    document.head.appendChild(script)
  }).catch((error) => {
    konvaPromise = null

    throw error
  })

  return konvaPromise
}

const PREVIEW_DEBUG = false
const PREVIEW_MOTIF_SIZE_RATIO = 0.1

const PREVIEW_REFERENCE_CANVAS_SIZE = 657
const PREVIEW_DEFAULT_FONT_SIZE_PT = 15
const CSS_PIXELS_PER_POINT = 96 / 72
const PREVIEW_STAMP_EFFECT_OFFSET = 0.45
const PREVIEW_STAMP_SHADOW_DARKNESS = 0.35
const PREVIEW_STAMP_SHADOW_OPACITY = 0.5
const PREVIEW_STAMP_HIGHLIGHT_LIGHTNESS = 0.35
const PREVIEW_STAMP_HIGHLIGHT_OPACITY = 0.5

const PREVIEW_TOP_THRESHOLD = 100 / 3
const PREVIEW_BOTTOM_THRESHOLD = (100 / 3) * 2

const PERSONALISATION_PREVIEW_UPLOAD_URL =
  'https://smythson.by-association-only.workers.dev/api/images'
const PERSONALISATION_PREVIEW_EXPORT_SIZE = 500
const PERSONALISATION_PREVIEW_EXPORT_QUALITY = 0.85

class ProductPersonalisation extends BAOCustomElement() {
  /*
   * Lifecycle
   */

  static get requiredElements () {
    return ['body']
  }

  connectedCallback () {
    super.connectedCallback()

    this.siteDrawers = getElement('site-drawers')

    this.setupPersonalisationContent()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'click', this.handleTrigger.bind(this))
    this.listeners.add(this, 'submit', this.handleContentSubmit.bind(this))
    this.listeners.add(
      this,
      'beforeinput',
      this.handleContentBeforeInput.bind(this),
    )
    this.listeners.add(this, 'input', this.handleContentInput.bind(this))
    this.listeners.add(this, 'change', this.handleContentChange.bind(this))
    this.listeners.add(this, 'click', this.handleContentClick.bind(this))
  }

  /*
   * Section loading and initialisation
   */

  async handleTrigger (event) {
    const target = event.target
    const trigger = target.closest(
      '[data-product-personalisation-external-el="trigger"]',
    )

    if (!trigger || trigger.getAttribute('aria-busy') === 'true') return

    trigger.setAttribute('aria-busy', true)

    loadKonva().catch(() => {})

    try {
      const loaded = await this.loadPersonalisation(
        trigger.dataset.productUrl,
        trigger,
      )

      if (!loaded) return

      this.siteDrawers.open('personalisation')
    } catch (error) {
      this.siteDrawers.close()
      console.error('Failed to load personalisation', error)
    } finally {
      trigger.setAttribute('aria-busy', false)
    }
  }

  async loadPersonalisation (productUrl, trigger = null) {
    const response = await sectionRenderer.fetchSingle(
      'personalisation',
      productUrl,
    )

    const newDOM = sectionRenderer.convertToDOM(response)
    const bodyContent = newDOM.querySelector('.drw-Personalisation')

    if (!bodyContent) {
      console.warn(
        '[ProductPersonalisation] No personalisation content was returned',
      )

      return false
    }

    this.resetPreview()
    this.els.body.element.innerHTML = bodyContent.outerHTML
    this.setupPersonalisationContent()

    if (!this.hasPersonalisationContent()) return false

    if (trigger) {
      this.setupPersonalisationMode(trigger)
    }

    this.els.body.element.getBoundingClientRect()

    return true
  }

  hasPersonalisationContent () {
    return [
      'form',
      'config',
      'id',
      'parent-id',
      'uuid',
      'submit',
      'price',
      'canvas',
    ].every((key) => this.els[key]?.element)
  }

  setupPersonalisationContent () {
    if (!this.hasPersonalisationContent()) return

    this.config = this.getConfig()
    this._isSubmitting = false
    this._nativeCartFields = null
    this._textErrors = []

    this.update()

    this.setupPreview()
      .then(() => {
        this.update()
      })
      .catch((error) => {
        console.error(
          '[ProductPersonalisation] Failed to setup preview',
          error,
        )
      })

    // Pre-warm the background image now rather than waiting for the first
    // add-to-cart — by the time generation actually runs, it's already
    // cached and this network fetch is off the critical path entirely.
    if (this.config?.productImage) {
      this.loadPersonalisationPreviewBackgroundImage(
        this.config.productImage,
      ).catch(() => {})
    }
  }

  getConfig () {
    try {
      return JSON.parse(this.els.config.element.textContent)
    } catch (error) {
      console.error('[ProductPersonalisation] Failed to parse config', error)

      return null
    }
  }

  /*
   * Personalisation mode
   */

  setupPersonalisationMode (trigger) {
    this.personalisationEditContext = null

    const mode = trigger.dataset.personalisationMode

    this.setSubmitMode(mode)

    if (mode === 'existing') {
      const parentLineKey = trigger.dataset.parentLineKey

      if (!parentLineKey) {
        console.warn(
          '[ProductPersonalisation] Missing existing parent line key',
        )

        return
      }

      this.personalisationEditContext = {
        mode,
        uuid: new Date().valueOf().toString(),
        parentLineKey,
        parentQuantity: parseInt(trigger.dataset.parentQuantity, 10) || 1,
        parentProperties: this.getPersonalisationParentProperties(trigger),
        textLineKey: '',
        motifLineKey: '',
      }

      return
    }

    if (mode !== 'edit') return

    const uuid = trigger.dataset.personalisationUuid
    const parentLineKey = trigger.dataset.parentLineKey

    if (!uuid || !parentLineKey) {
      console.warn('[ProductPersonalisation] Missing edit context')

      return
    }

    this.personalisationEditContext = {
      mode,
      uuid,
      parentLineKey,
      textLineKey: trigger.dataset.textLineKey || '',
      motifLineKey: trigger.dataset.motifLineKey || '',
    }

    if (this.els.text?.element) {
      this.els.text.element.value = trigger.dataset.text || ''
    }

    this.setCheckedOption('font', trigger.dataset.font)
    this.setCheckedOption('colour', trigger.dataset.colour)
    this.setCheckedOption('position', trigger.dataset.position)
    this.setCheckedOption('motif', trigger.dataset.motif || '')

    this.update()
  }

  setSubmitMode (mode) {
    const isEdit = mode === 'edit'

    if (this.els['submit-add-label']?.element) {
      this.els['submit-add-label'].element.hidden = isEdit
    }

    if (this.els['submit-update-label']?.element) {
      this.els['submit-update-label'].element.hidden = !isEdit
    }
  }

  getPersonalisationParentProperties (trigger) {
    try {
      return JSON.parse(trigger.dataset.parentProperties || '{}')
    } catch (error) {
      console.warn(
        '[ProductPersonalisation] Failed to parse parent properties',
        error,
      )

      return {}
    }
  }

  setCheckedOption (key, value) {
    const option = this.els[key]?.elements?.find((element) => {
      return element.value === value
    })

    if (!option) return

    option.checked = true
  }

  /*
   * Delegated content events
   */

  handleContentSubmit (event) {
    if (!event.target.matches('[data-product-personalisation-el="form"]')) {
      return
    }

    this.onSubmitHandler(event)
  }

  handleContentBeforeInput (event) {
    if (!event.target.matches('[data-product-personalisation-el="text"]')) {
      return
    }

    this.onTextBeforeInput(event)
  }

  handleContentInput (event) {
    if (!event.target.matches('[data-product-personalisation-el="text"]')) {
      return
    }

    this.onTextInput(event)
  }

  handleContentChange (event) {
    const key = event.target.dataset.productPersonalisationEl

    if (!['font', 'colour', 'motif', 'position'].includes(key)) return

    const previousTextErrors = [...this._textErrors]

    if (key === 'font') {
      this.setTextInputFeedback()
    }

    this.update()

    if (key !== 'font') return

    const hasNewTextError = this._textErrors.some((error) => {
      return !previousTextErrors.includes(error)
    })

    if (hasNewTextError) {
      this.revealTextError()
    }
  }

  handleContentClick (event) {
    const trigger = event.target.closest(
      '[data-product-personalisation-el="motif-remove"]',
    )

    if (!trigger) return

    this.removeMotif()
  }

  /*
   * State and updates
   */

  update () {
    const state = this.getState()

    this.updateTextInput(state)
    this.updateLabels(state)
    this.updateTextError(state)
    this.updatePrice(state)
    this.updateSubmit(state)
    this.updateMotifRemove(state)
    this.updatePreview(this.getPreviewState(state))
  }

  getPreviewState (state) {
    if (!this.previewPositionOverride) return state

    return {
      ...state,
      position: {
        value: '__debug__',
        label: 'Debug position',
        dataset: {
          ...(state.position?.dataset || {}),
          horizontalPosition: String(
            this.previewPositionOverride.horizontalPosition,
          ),
          verticalPosition: String(
            this.previewPositionOverride.verticalPosition,
          ),
          textAlignment: this.previewPositionOverride.textAlignment,
          expandDirection: this.previewPositionOverride.expandDirection,
        },
      },
    }
  }

  getState () {
    const font = this.getCheckedElement('font')
    const colour = this.getCheckedElement('colour')
    const motif = this.getCheckedElement('motif')
    const position = this.getCheckedElement('position')

    return {
      parentVariantId: this.els['parent-id'].element.value,
      text: this.els.text?.element?.value || '',

      font: this.getOptionState(font),
      colour: this.getOptionState(colour),
      motif: this.getOptionState(motif),
      position: this.getOptionState(position),
    }
  }

  getCheckedElement (key) {
    return this.els[key]?.elements?.find((el) => el.checked) || null
  }

  getOptionState (el) {
    if (!el) return null

    return {
      value: el.value,
      label: el.dataset.label || el.value,
      dataset: { ...el.dataset },
    }
  }

  /*
   * Text input and validation
   */

  onTextBeforeInput (e) {
    if (e.isComposing) return

    const state = this.getState()
    const allowableCharacters = state.font?.dataset?.allowableCharacters

    if (!allowableCharacters) return
    if (!e.data) return

    if (this.isAllowedText(e.data, allowableCharacters)) {
      this.setTextInputFeedback()

      return
    }

    e.preventDefault()

    if (this.getTextErrors(state).length > 0) {
      this.setTextInputFeedback()

      return
    }

    this.setTextInputFeedback(
      this.getAllowedCharactersMessage(allowableCharacters),
    )
  }

  onTextInput (e) {
    const input = e.target
    const selectionStart = input.selectionStart
    const selectionEnd = input.selectionEnd

    input.value = input.value.toUpperCase()

    if (selectionStart !== null && selectionEnd !== null) {
      input.setSelectionRange(selectionStart, selectionEnd)
    }

    this.setTextInputFeedback()
    this.update()
  }

  setTextInputFeedback (message = '') {
    const feedback = this.els['text-feedback']?.element

    if (!feedback) return

    feedback.textContent = message
    feedback.hidden = !message
  }

  updateTextInput (state) {
    if (!this.els.text?.element) return

    const input = this.els.text.element
    const maxCharacters = this.getMaxCharacters(state)

    input.maxLength = maxCharacters

    if (this.els['max-character-count']?.element) {
      this.els['max-character-count'].element.innerText = maxCharacters
    }

    if (this.els['character-count']?.element) {
      this.els['character-count'].element.innerText = input.value.length
    }
  }

  getMaxCharacters (state) {
    return parseInt(state.font?.dataset?.maxCharacters, 10) || 15
  }

  getChargeableTextLength (text) {
    if (!text) return 0

    return text.replace(/\s/g, '').length
  }

  isWithinMaxCharacters (text, maxCharacters) {
    if (!text || !maxCharacters) return true

    return text.length <= maxCharacters
  }

  updateTextError (state) {
    const error = this.els['text-error']?.element
    const input = this.els.text?.element
    const textCount = this.els['text-count']?.element

    const errors = this.getTextErrors(state)
    const invalid = errors.length > 0

    this._textErrors = errors

    if (invalid) {
      this.setTextInputFeedback()
    }

    if (input) {
      input.setAttribute('aria-invalid', invalid ? 'true' : 'false')
    }

    if (textCount) {
      textCount.toggleAttribute('data-invalid', invalid)
    }

    if (!error) return

    error.hidden = !invalid
    error.replaceChildren()

    errors.forEach((message) => {
      const item = document.createElement('p')

      item.className =
        'frm-Form_ValidationText frm-Form_ValidationText-error fz-12_150'
      item.textContent = message

      error.appendChild(item)
    })
  }

  revealTextError () {
    const section = this.els['text-section']?.element
    const error = this.els['text-error']?.element

    if (!section || !error || error.hidden) return

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollContainer = this.getScrollableParent(section)
        const sectionRect = section.getBoundingClientRect()

        const documentScroller =
          document.scrollingElement || document.documentElement

        const isDocumentScroller = scrollContainer === documentScroller

        const containerRect = isDocumentScroller
          ? {
              top: 0,
              bottom: window.visualViewport?.height || window.innerHeight,
            }
          : scrollContainer.getBoundingClientRect()

        const sectionIsVisible =
          sectionRect.top >= containerRect.top &&
          sectionRect.bottom <= containerRect.bottom

        if (sectionIsVisible) return

        const currentScrollTop = isDocumentScroller
          ? window.scrollY
          : scrollContainer.scrollTop

        const targetScrollTop =
          currentScrollTop + sectionRect.top - containerRect.top

        const scrollOptions = {
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth',
        }

        if (isDocumentScroller) {
          window.scrollTo(scrollOptions)

          return
        }

        scrollContainer.scrollTo(scrollOptions)
      })
    })
  }

  getScrollableParent (element) {
    let parent = element.parentElement

    while (parent && parent !== document.body) {
      const styles = window.getComputedStyle(parent)

      const hasScrollableOverflow = ['auto', 'scroll', 'overlay'].includes(
        styles.overflowY,
      )

      const hasScrollableContent = parent.scrollHeight > parent.clientHeight

      if (hasScrollableOverflow && hasScrollableContent) {
        return parent
      }

      parent = parent.parentElement
    }

    return document.scrollingElement || document.documentElement
  }

  getTextErrors (state) {
    const errors = []

    if (!state.text) return errors

    const maxCharacters = this.getMaxCharacters(state)
    const allowableCharacters = state.font?.dataset?.allowableCharacters

    if (this.getChargeableTextLength(state.text) < 1) {
      errors.push('Please enter at least one character.')
    }

    if (!this.isWithinMaxCharacters(state.text, maxCharacters)) {
      errors.push(`This font allows a maximum of ${maxCharacters} characters.`)
    }

    if (!this.isAllowedText(state.text, allowableCharacters)) {
      errors.push(this.getAllowedCharactersMessage(allowableCharacters))
    }

    return errors
  }

  normaliseAllowableCharacters (allowableCharacters) {
    if (!allowableCharacters) return ''

    return allowableCharacters.toLowerCase().replace(/[,\s]+/g, '')
  }

  isAllowedText (text, allowableCharacters) {
    if (!text || !allowableCharacters) return true

    try {
      const validationText = text.replace(/\s/g, '')
      const normalisedCharacters =
        this.normaliseAllowableCharacters(allowableCharacters)

      if (!normalisedCharacters) return true

      const regex = new RegExp(`^[${normalisedCharacters}]*$`, 'i')

      return regex.test(validationText)
    } catch (error) {
      console.warn(
        '[ProductPersonalisation] Invalid allowable characters:',
        allowableCharacters,
      )

      return true
    }
  }

  getValidationMessage (key) {
    const message = this.config?.validation?.[key]

    if (typeof message !== 'string' || message.length === 0) {
      throw new Error(
        `[ProductPersonalisation] Missing validation string: ${key}`,
      )
    }

    return message
  }

  getAllowedCharactersMessage (allowableCharacters) {
    const normalisedCharacters =
      this.normaliseAllowableCharacters(allowableCharacters)

    if (!normalisedCharacters) {
      return this.getValidationMessage('invalidCharacters')
    }

    if (normalisedCharacters === 'a-z') {
      return this.getValidationMessage('lettersOnly')
    }

    if (normalisedCharacters === '0-9') {
      return this.getValidationMessage('numbersOnly')
    }

    if (
      normalisedCharacters === 'a-z0-9' ||
      normalisedCharacters === '0-9a-z'
    ) {
      return this.getValidationMessage('lettersAndNumbersOnly')
    }

    return this.getValidationMessage('allowedCharacters').replace(
      '{{ characters }}',
      allowableCharacters,
    )
  }

  /*
   * Option display
   */

  updateLabels (state) {
    if (this.els['font-label']?.element && state.font) {
      const pointSize = state.font.dataset.pointSize

      this.els['font-label'].element.innerText = pointSize
        ? `${state.font.label}, ${pointSize}pt`
        : state.font.label
    }

    if (this.els['colour-label']?.element && state.colour) {
      this.els['colour-label'].element.innerText = state.colour.label
    }

    if (this.els['motif-label']?.element && state.motif) {
      this.els['motif-label'].element.innerText = state.motif.label || 'None'
    }
  }

  removeMotif () {
    const noneMotif = this.els.motif?.elements?.find(
      (motif) => motif.value === '',
    )

    if (!noneMotif) return

    noneMotif.checked = true

    noneMotif.dispatchEvent(
      new Event('change', {
        bubbles: true,
      }),
    )
  }

  updateMotifRemove (state) {
    if (!this.els['motif-remove']?.element) return

    this.els['motif-remove'].element.hidden = !state.motif?.value
  }

  /*
   * Pricing and submit state
   */

  updatePrice (state) {
    const price = this.getPersonalisationPrice(state)

    this.els.price.element.innerText = formatMoney(price)
  }

  getPersonalisationPrice (state) {
    let price = 0

    const textVariant = this.getTextPricingVariant(state)
    const motifVariant = this.getMotifPricingVariant(state)

    if (textVariant) {
      price += Number(textVariant.price)
    }

    if (motifVariant) {
      price += Number(motifVariant.price)
    }

    return price
  }

  getTextPricingVariant (state) {
    if (!state.text) return null

    if (this.getTextErrors(state).length > 0) {
      return null
    }

    const chargeableLength = this.getChargeableTextLength(state.text)

    if (chargeableLength < 1) return null

    const variants = this.config?.textPricing?.variants || []

    return variants[chargeableLength - 1] || null
  }

  getMotifPricingVariant (state) {
    if (!state.motif?.value) return null

    const variants = this.config?.motifPricing?.variants || []

    return variants.find((variant) => variant.available) || variants[0] || null
  }

  updateSubmit (state) {
    this.els.submit.element.disabled = !this.isValid(state)
  }

  isValid (state) {
    const hasText = state.text.length > 0
    const hasMotif = Boolean(state.motif?.value)

    if (!state.parentVariantId) return false
    if (!hasText && !hasMotif) return false
    if (!state.position?.value) return false
    if (!state.colour?.value) return false

    if (hasText && !state.font?.value) return false
    if (hasText && this.getTextErrors(state).length > 0) return false
    if (hasText && !this.getTextPricingVariant(state)) return false

    if (hasMotif && !this.getMotifPricingVariant(state)) return false

    return true
  }

  /*
   * Cart submission
   */

  getCartSectionOptions () {
    if (!document.getElementById('template-cart')) {
      return undefined
    }

    return {
      sections: (sectionRenderer.cartSections || [])
        .map((section) => section.section)
        .filter(Boolean),
    }
  }

  async onSubmitHandler (e) {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (this._isSubmitting) {
      return false
    }

    const state = this.getState()

    if (!this.isValid(state)) {
      this.update()

      return false
    }

    const cartState = await this.addToCart(state)

    if (cartState) {
      this.personalisationEditContext = null

      this.resetForm()
    }

    return false
  }

  async addToCart (state) {
    if (this._isSubmitting) {
      return
    }

    this._isSubmitting = true
    this.els.submit.element.disabled = true
    this.els.submit.element.setAttribute('aria-busy', 'true')

    const uuid =
      this.personalisationEditContext?.uuid || new Date().valueOf().toString()
    const needsPreview = Boolean(state.text || state.motif?.value)

    // Snapshot the live preview NOW, synchronously — resetForm() (called by
    // the caller once addToCart resolves) clears the live motif/text state,
    // and that happens well before the background export/upload below would
    // otherwise get around to reading it.
    const previewGroupSnapshot = needsPreview
      ? this.snapshotPersonalisationPreviewGroup()
      : null

    try {
      let parentLineInfo = null

      if (this.personalisationEditContext?.mode === 'edit') {
        await this.removeExistingPersonalisationItems()
      }

      if (
        this.personalisationEditContext?.mode === 'existing' ||
        this.personalisationEditContext?.mode === 'edit'
      ) {
        parentLineInfo = await this.prepareExistingPersonalisationParent(
          uuid,
          needsPreview,
        )
      }

      const items = this.getCartItems(state, uuid)

      this.syncCartItemsToForm(items)
      this.disableNativeCartFields()

      const cartState = await Cart.addItemFromForm(
        this.els.form.element,
        this.getCartSectionOptions(),
      )

      if (needsPreview) {
        // Deliberately not awaited — add-to-cart must not wait on preview
        // generation/upload. The cart line shows a "generating" spinner
        // (driven purely by the `_personalisation_preview_pending`
        // property, see push-cart-item.liquid) until this resolves and
        // swaps the real image in via a follow-up property update.
        this.attachPersonalisationPreview(
          previewGroupSnapshot,
          uuid,
          parentLineInfo,
          cartState,
        )
      }

      return cartState
    } finally {
      this.restoreNativeCartFields()
      this.clearGeneratedCartInputs()

      this._isSubmitting = false
      this.els.submit.element.setAttribute('aria-busy', 'false')

      this.update()
    }
  }

  /*
   * Generates and uploads the composite preview in the background (after
   * add-to-cart has already resolved) and attaches it to the parent line via
   * a follow-up property update. Always clears `_personalisation_preview_pending`
   * on completion, whether or not the upload succeeded, so the cart line's
   * "generating" state never gets stuck.
   */
  async attachPersonalisationPreview (
    clonedGroup,
    uuid,
    parentLineInfo,
    cartState,
  ) {
    try {
      const parent =
        parentLineInfo || this.findPersonalisationParentLine(uuid, cartState)

      if (!parent) {
        console.warn(
          '[ProductPersonalisation] Could not find the personalisation parent line to attach a preview to',
        )
        clonedGroup?.destroy()

        return
      }

      const previewUrl = await this.getPersonalisationPreviewUrl(clonedGroup)

      await Cart.updateItemProperties(parent.key, parent.quantity, {
        ...parent.properties,
        _personalisation_preview_pending: '',
        ...(previewUrl ? { _personalisation_preview: previewUrl } : {}),
      })
    } catch (error) {
      console.warn(
        '[ProductPersonalisation] Failed to attach the preview image to the cart line',
        error,
      )
    }
  }

  findPersonalisationParentLine (uuid, cartState) {
    const cart = cartState?.cart || cartState

    const parentItem = cart?.items?.find((item) => {
      return (
        item.properties?._personalisation_uuid === uuid &&
        String(item.properties?._has_personalisation) === 'true'
      )
    })

    if (!parentItem) return null

    return {
      key: parentItem.key,
      quantity: parentItem.quantity,
      properties: parentItem.properties,
    }
  }

  async prepareExistingPersonalisationParent (uuid, needsPreview) {
    const context = this.personalisationEditContext

    if (!context?.parentLineKey) return null

    const properties = {
      ...context.parentProperties,
      _personalisation_uuid: uuid,
      _has_personalisation: 'true',
      ...(needsPreview ? { _personalisation_preview_pending: 'true' } : {}),
    }

    const state = await Cart.updateItemProperties(
      context.parentLineKey,
      context.parentQuantity,
      properties,
      {
        sections: [],
      },
    )

    if (!state) {
      throw new Error('Failed to update the personalisation parent')
    }

    const cart = state.cart || state

    const parentItem = cart.items.find((item) => {
      return (
        item.properties?._personalisation_uuid === uuid &&
        String(item.properties?._has_personalisation) === 'true'
      )
    })

    if (!parentItem) {
      throw new Error('Could not find the updated personalisation parent')
    }

    context.parentLineKey = parentItem.key

    return {
      key: parentItem.key,
      quantity: parentItem.quantity,
      properties: parentItem.properties,
    }
  }

  async removeExistingPersonalisationItems () {
    const lineKeys = [
      this.personalisationEditContext.textLineKey,
      this.personalisationEditContext.motifLineKey,
    ].filter(Boolean)

    if (lineKeys.length === 0) return

    const state = await Cart.removeItems(lineKeys, {
      sections: [],
    })

    if (!state) {
      throw new Error('Failed to remove existing personalisation')
    }

    const cart = state.cart || state

    const parentItem = cart.items.find((item) => {
      return (
        item.properties?._personalisation_uuid ===
          this.personalisationEditContext.uuid &&
        String(item.properties?._has_personalisation) === 'true'
      )
    })

    if (!parentItem) {
      throw new Error('Could not find the parent personalisation item')
    }

    this.personalisationEditContext.parentLineKey = parentItem.key
  }

  /*
   * Cart item construction
   */

  getCartItems (state, uuid) {
    const items = []

    if (!this.personalisationEditContext) {
      items.push(this.getParentCartItem(state, uuid))
    }

    if (state.text) {
      items.push(this.getTextCartItem(state, uuid))
    }

    if (state.motif?.value) {
      items.push(this.getMotifCartItem(state, uuid))
    }

    return items.filter((item) => item?.id)
  }

  getParentCartItem (state, uuid) {
    const needsPreview = Boolean(state.text || state.motif?.value)

    return {
      id: Number(state.parentVariantId),
      quantity: 1,
      properties: {
        _personalisation_uuid: uuid,
        _has_personalisation: 'true',
        ...(needsPreview ? { _personalisation_preview_pending: 'true' } : {}),
      },
    }
  }

  getPersonalisationParent (state) {
    if (this.personalisationEditContext?.parentLineKey) {
      return {
        parent_line_key: this.personalisationEditContext.parentLineKey,
      }
    }

    return {
      parent_id: Number(state.parentVariantId),
    }
  }

  getTextCartItem (state, uuid) {
    const variant = this.getTextPricingVariant(state)

    if (!variant) return null

    return {
      id: Number(variant.id),
      quantity: 1,
      ...this.getPersonalisationParent(state),
      properties: {
        _personalisation_uuid: uuid,
        _parent_variant_id: state.parentVariantId,
        _personalisation_type: 'text',
        Text: state.text,
        Font: state.font?.label || '',
        'Font size': state.font?.dataset?.pointSize
          ? `${state.font.dataset.pointSize}pt`
          : '',
        Colour: state.colour?.label || '',
        Position: state.position?.label || '',
      },
    }
  }

  getMotifCartItem (state, uuid) {
    const variant = this.getMotifPricingVariant(state)

    if (!variant) return null

    return {
      id: Number(variant.id),
      quantity: 1,
      ...this.getPersonalisationParent(state),
      properties: {
        _personalisation_uuid: uuid,
        _parent_variant_id: state.parentVariantId,
        _personalisation_type: 'motif',
        Motif: state.motif?.label || '',
        Colour: state.colour?.label || '',
        Position: state.position?.label || '',
      },
    }
  }

  /*
   * Cart form payload
   */

  syncCartItemsToForm (items) {
    const form = this.els.form.element

    this.clearGeneratedCartInputs()

    items.forEach((item, index) => {
      this.appendHiddenInput(form, `items[${index}][id]`, item.id)
      this.appendHiddenInput(
        form,
        `items[${index}][quantity]`,
        item.quantity || 1,
      )

      if (item.parent_line_key) {
        this.appendHiddenInput(
          form,
          `items[${index}][parent_line_key]`,
          item.parent_line_key,
        )
      } else if (item.parent_id) {
        this.appendHiddenInput(
          form,
          `items[${index}][parent_id]`,
          item.parent_id,
        )
      }

      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return

        this.appendHiddenInput(
          form,
          `items[${index}][properties][${key}]`,
          value,
        )
      })
    })
  }

  appendHiddenInput (form, name, value) {
    const input = document.createElement('input')

    input.type = 'hidden'
    input.name = name
    input.value = value
    input.dataset.personalisationCartItem = ''

    form.appendChild(input)
  }

  clearGeneratedCartInputs () {
    this.els.form.element
      .querySelectorAll('[data-personalisation-cart-item]')
      .forEach((input) => {
        input.remove()
      })
  }

  disableNativeCartFields () {
    const form = this.els.form.element

    const nativeCartFields = form.querySelectorAll(
      ['[name="id"]', '[name="quantity"]', '[name^="properties["]'].join(','),
    )

    this._nativeCartFields = Array.from(nativeCartFields)
      .filter((input) => !input.dataset.personalisationCartItem)
      .map((input) => {
        return {
          input,
          disabled: input.disabled,
        }
      })

    this._nativeCartFields.forEach(({ input }) => {
      input.disabled = true
    })
  }

  restoreNativeCartFields () {
    if (!this._nativeCartFields) return

    this._nativeCartFields.forEach(({ input, disabled }) => {
      input.disabled = disabled
    })

    this._nativeCartFields = null
  }

  resetForm () {
    this.els.form.element.reset()

    if (this.els.uuid?.element) {
      this.els.uuid.element.value = ''
    }

    this.setTextInputFeedback()
    this.update()
  }

  /*
   * Preview setup and teardown
   */

  async setupPreview () {
    if (this.previewStage) return

    const Konva = await loadKonva()

    if (this.previewStage) return

    const container = this.els.canvas.element

    this.previewStage = new Konva.Stage({
      container,
      width: 1,
      height: 1,
      listening: false,
    })

    this.previewLayer = new Konva.Layer({
      listening: false,
    })

    this.previewStage.add(this.previewLayer)

    this.setupPreviewGroup()
    this.setupPreviewMotif()
    this.setupPreviewText()
    this.setupPreviewDebug()

    this.previewResizeObserver = new ResizeObserver(() => {
      this.requestPreviewResize()
    })

    this.previewResizeObserver.observe(container)

    this.requestPreviewResize()
  }

  setupPreviewGroup () {
    if (!this.previewLayer || this.previewGroup) return

    this.previewGroup = new window.Konva.Group({
      listening: false,
    })

    this.previewLayer.add(this.previewGroup)
  }

  setupPreviewMotif () {
    if (!this.previewGroup || this.previewMotif) return

    const motifAttrs = {
      visible: false,
      listening: false,
    }

    this.previewMotifShadow = new window.Konva.Image({ ...motifAttrs })
    this.previewMotifHighlight = new window.Konva.Image({ ...motifAttrs })
    this.previewMotif = new window.Konva.Image({ ...motifAttrs })

    this.previewGroup.add(
      this.previewMotifShadow,
      this.previewMotifHighlight,
      this.previewMotif,
    )
  }

  setupPreviewText () {
    if (!this.previewGroup || this.previewText) return

    const textAttrs = {
      text: '',
      fontFamily: 'Arial',
      fontStyle: 'normal',
      fontSize: PREVIEW_DEFAULT_FONT_SIZE_PT * CSS_PIXELS_PER_POINT,
      letterSpacing: 0,
      align: 'center',
      verticalAlign: 'top',
      wrap: 'none',
      listening: false,
    }

    this.previewTextShadow = new window.Konva.Text({
      ...textAttrs,
      fill: this.getPreviewStampShadowColour('#000000'),
    })

    this.previewTextHighlight = new window.Konva.Text({
      ...textAttrs,
      fill: this.getPreviewStampHighlightColour('#000000'),
    })

    this.previewText = new window.Konva.Text({
      ...textAttrs,
      fill: '#000000',
    })

    this.previewGroup.add(
      this.previewTextShadow,
      this.previewTextHighlight,
      this.previewText,
    )
  }

  setupPreviewDebug () {
    if (!PREVIEW_DEBUG || !this.previewLayer || this.previewDebugGroup) return

    this.previewDebugGroup = new window.Konva.Group({
      listening: false,
    })

    this.previewDebugComposition = new window.Konva.Rect({
      stroke: '#00c853',
      strokeWidth: 1,
      dash: [4, 4],
      visible: false,
      listening: false,
    })

    this.previewDebugText = new window.Konva.Rect({
      stroke: '#ff9100',
      strokeWidth: 1,
      dash: [2, 2],
      visible: false,
      listening: false,
    })

    this.previewDebugMotif = new window.Konva.Rect({
      stroke: '#2979ff',
      strokeWidth: 1,
      dash: [2, 2],
      visible: false,
      listening: false,
    })

    this.previewDebugAnchor = new window.Konva.Circle({
      x: 0,
      y: 0,
      radius: 3,
      fill: '#ff1744',
      listening: false,
    })

    this.previewDebugGroup.add(
      this.previewDebugComposition,
      this.previewDebugText,
      this.previewDebugMotif,
      this.previewDebugAnchor,
    )

    this.previewLayer.add(this.previewDebugGroup)
  }

  resetPreview () {
    if (this.previewResizeFrame) {
      cancelAnimationFrame(this.previewResizeFrame)
    }

    this.previewMotifLoadId = (this.previewMotifLoadId || 0) + 1

    this.previewResizeObserver?.disconnect()
    this.previewStage?.destroy()

    this.previewResizeFrame = null
    this.previewResizeObserver = null
    this.previewFontFamily = null

    this.previewMotifSource = null
    this.previewMotifImage = null
    this.previewMotifCanvas = null
    this.previewMotifDisplayCanvas = null
    this.previewMotifShadowCanvas = null
    this.previewMotifHighlightCanvas = null
    this.previewMotifBounds = null
    this.previewMotifColour = null
    this.previewMotifAppliedColour = null
    this.previewMotifScale = null
    this.previewMotifShadow = null
    this.previewMotifHighlight = null
    this.previewMotif = null

    this.previewTextShadow = null
    this.previewTextHighlight = null
    this.previewText = null
    this.previewGroup = null

    this.previewDebugComposition = null
    this.previewDebugText = null
    this.previewDebugMotif = null
    this.previewDebugAnchor = null
    this.previewDebugGroup = null

    this.previewLayer = null
    this.previewStage = null

    this.previewBackgroundSource = null
    this.previewBackgroundImage = null

    this.previewPositionOverride = null
    this.previewFontScaleOverride = null
    this.previewMotifScaleOverride = null
  }

  /*
   * Preview positioning and typography
   */

  updatePreview (state) {
    if (!this.previewLayer || !this.previewStage) return

    const fontFamily = state.font?.dataset?.fontFamily || 'Arial'
    const colour = state.colour?.dataset?.colour || '#000000'

    this.previewTextAlignment = this.getPreviewTextAlignment(state)
    this.previewExpandDirection = this.getPreviewExpandDirection(state)
    this.previewVerticalAnchor = this.getPreviewVerticalAnchor(state)

    this.updatePreviewPosition(state)

    if (this.previewText) {
      const text = state.text || ''
      const textNodes = [
        this.previewTextShadow,
        this.previewTextHighlight,
        this.previewText,
      ]

      textNodes.forEach((node) => {
        node?.setAttrs({
          text,
          fontFamily,
        })
      })

      this.previewText.fill(colour)
      this.previewTextShadow?.fill(this.getPreviewStampShadowColour(colour))
      this.previewTextHighlight?.fill(
        this.getPreviewStampHighlightColour(colour),
      )

      this.updatePreviewTextLayout(state)
      this.loadPreviewFont(fontFamily)
    }

    this.updatePreviewMotif(state)
    this.updatePreviewCompositionLayout()

    this.previewLayer.batchDraw()
  }

  updatePreviewPosition (state) {
    if (!this.previewStage || !this.previewGroup) return

    const horizontalPosition = this.getPreviewPositionPercentage(
      state.position?.dataset?.horizontalPosition,
      50,
    )
    const verticalPosition = this.getPreviewPositionPercentage(
      state.position?.dataset?.verticalPosition,
      50,
    )

    const position = {
      x: this.previewStage.width() * (horizontalPosition / 100),
      y: this.previewStage.height() * (verticalPosition / 100),
    }

    this.previewGroup.position(position)
    this.previewDebugGroup?.position(position)
  }

  getPreviewStampShadowColour (colour) {
    return this.getPreviewStampEffectColour(
      colour,
      0,
      PREVIEW_STAMP_SHADOW_DARKNESS,
      PREVIEW_STAMP_SHADOW_OPACITY,
    )
  }

  getPreviewStampHighlightColour (colour) {
    return this.getPreviewStampEffectColour(
      colour,
      255,
      PREVIEW_STAMP_HIGHLIGHT_LIGHTNESS,
      PREVIEW_STAMP_HIGHLIGHT_OPACITY,
    )
  }

  getPreviewStampEffectColour (colour, target, amount, opacity) {
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colour?.trim() || '')

    if (!match) {
      return `rgba(${target}, ${target}, ${target}, ${opacity})`
    }

    const hex =
      match[1].length === 3
        ? match[1]
          .split('')
          .map((character) => character + character)
          .join('')
        : match[1]

    const value = Number.parseInt(hex, 16)
    const red = (value >> 16) & 255
    const green = (value >> 8) & 255
    const blue = value & 255
    const mix = (channel) => Math.round(channel + (target - channel) * amount)

    return `rgba(${mix(red)}, ${mix(green)}, ${mix(blue)}, ${opacity})`
  }

  getPreviewPositionPercentage (value, fallback = 50) {
    const percentage = Number.parseFloat(value)

    if (!Number.isFinite(percentage)) {
      return fallback
    }

    return Math.min(100, Math.max(0, percentage))
  }

  getPreviewTextAlignment (state) {
    const value = state.position?.dataset?.textAlignment?.trim().toLowerCase()

    if (value === 'left') {
      return 'left'
    }

    if (value === 'right') {
      return 'right'
    }

    return 'center'
  }

  getPreviewExpandDirection (state) {
    const value = state.position?.dataset?.expandDirection
      ?.trim()
      .toLowerCase()

    if (value === 'left') {
      return 'left'
    }

    if (value === 'right') {
      return 'right'
    }

    return 'center'
  }

  getPreviewVerticalAnchor (state) {
    const verticalPosition = this.getPreviewPositionPercentage(
      state.position?.dataset?.verticalPosition,
      50,
    )

    if (verticalPosition <= PREVIEW_TOP_THRESHOLD) {
      return 'top'
    }

    if (verticalPosition >= PREVIEW_BOTTOM_THRESHOLD) {
      return 'bottom'
    }

    return 'center'
  }

  getPreviewCanvasScale () {
    if (!this.previewStage) return 1

    const canvasSize = Math.min(
      this.previewStage.width(),
      this.previewStage.height(),
    )

    if (!Number.isFinite(canvasSize) || canvasSize <= 0) {
      return 1
    }

    return canvasSize / PREVIEW_REFERENCE_CANVAS_SIZE
  }

  getPreviewFontSize (state) {
    const baseFontSizePt = Number.parseFloat(state.font?.dataset?.size)
    const configuredFontSizePt = Number.parseFloat(
      state.font?.dataset?.previewSize,
    )
    const hasDebugScale = Number.isFinite(this.previewFontScaleOverride)

    let fontSizePt = configuredFontSizePt

    if (
      hasDebugScale &&
      Number.isFinite(baseFontSizePt) &&
      baseFontSizePt > 0
    ) {
      fontSizePt = baseFontSizePt * (1 + this.previewFontScaleOverride)
    }

    if (!Number.isFinite(fontSizePt) || fontSizePt <= 0) {
      fontSizePt =
        Number.isFinite(baseFontSizePt) && baseFontSizePt > 0
          ? baseFontSizePt
          : PREVIEW_DEFAULT_FONT_SIZE_PT
    }

    const fontSizePx = fontSizePt * CSS_PIXELS_PER_POINT

    return fontSizePx * this.getPreviewCanvasScale()
  }

  getPreviewLetterSpacing (state, fontSize) {
    const configuredPercentage = Number.parseFloat(
      state.font?.dataset?.letterSpacing,
    )

    if (!Number.isFinite(configuredPercentage)) {
      return 0
    }

    return fontSize * (configuredPercentage / 100)
  }

  updatePreviewTextLayout (state) {
    if (!this.previewText) return

    const fontSize = this.getPreviewFontSize(state)
    const letterSpacing = this.getPreviewLetterSpacing(state, fontSize)
    const attrs = {
      fontSize,
      letterSpacing,
      align: this.previewTextAlignment || 'center',
    }

    this.previewTextShadow?.setAttrs(attrs)
    this.previewTextHighlight?.setAttrs(attrs)
    this.previewText.setAttrs(attrs)
  }

  getPreviewTextWidth () {
    if (!this.previewText) return 0

    const text = this.previewText.text()

    if (!text) return 0

    const measuredWidth = this.previewText.measureSize(text).width
    const letterSpacing = this.previewText.letterSpacing() || 0

    return Math.max(0, measuredWidth + letterSpacing * text.length)
  }

  updatePreviewCompositionLayout () {
    if (!this.previewStage || !this.previewText || !this.previewGroup) {
      return
    }

    const stageWidth = this.previewStage.width()
    const stageHeight = this.previewStage.height()

    const hasText = this.previewText.text().trim().length > 0
    const motifImage = this.previewMotif?.image() || null

    const hasMotif = Boolean(this.previewMotifImage && motifImage)

    const textWidth = hasText ? this.getPreviewTextWidth() : 0
    const textHeight = hasText ? Math.ceil(this.previewText.fontSize()) : 0

    let motifWidth = 0
    let motifHeight = 0

    if (hasMotif) {
      const imageWidth = motifImage.naturalWidth || motifImage.width
      const imageHeight = motifImage.naturalHeight || motifImage.height

      if (imageWidth && imageHeight) {
        const motifScale = this.previewMotifScale || 1

        const maxWidth = stageWidth * PREVIEW_MOTIF_SIZE_RATIO * motifScale
        const maxHeight = stageHeight * PREVIEW_MOTIF_SIZE_RATIO * motifScale

        const imageScale = Math.min(
          maxWidth / imageWidth,
          maxHeight / imageHeight,
        )

        motifWidth = imageWidth * imageScale
        motifHeight = imageHeight * imageScale
      }
    }

    const gap = hasMotif && hasText ? this.config.motifTextGap : 0

    const compositionWidth = Math.max(textWidth, motifWidth)
    const compositionHeight = motifHeight + gap + textHeight

    if (compositionWidth <= 0 || compositionHeight <= 0) {
      this.previewTextShadow?.hide()
      this.previewTextHighlight?.hide()
      this.previewText.hide()
      this.previewMotifShadow?.hide()
      this.previewMotifHighlight?.hide()
      this.previewMotif?.hide()

      this.updatePreviewDebug({
        hasText: false,
        hasMotif: false,
      })

      return
    }

    let compositionX = -(compositionWidth / 2)
    let compositionY = -(compositionHeight / 2)

    if (this.previewExpandDirection === 'left') {
      compositionX = -compositionWidth
    }

    if (this.previewExpandDirection === 'right') {
      compositionX = 0
    }

    if (this.previewVerticalAnchor === 'top') {
      compositionY = 0
    }

    if (this.previewVerticalAnchor === 'bottom') {
      compositionY = -compositionHeight
    }

    const textY = compositionY + motifHeight + gap
    const motifX = compositionX + (compositionWidth - motifWidth) / 2
    const effectOffset =
      PREVIEW_STAMP_EFFECT_OFFSET * this.getPreviewCanvasScale()

    if (hasMotif) {
      const motifAttrs = {
        width: motifWidth,
        height: motifHeight,
        visible: true,
      }

      this.previewMotifShadow?.setAttrs({
        ...motifAttrs,
        x: motifX - effectOffset,
        y: compositionY - effectOffset,
      })

      this.previewMotifHighlight?.setAttrs({
        ...motifAttrs,
        x: motifX + effectOffset,
        y: compositionY + effectOffset,
      })

      this.previewMotif.setAttrs({
        ...motifAttrs,
        x: motifX,
        y: compositionY,
      })
    } else {
      this.previewMotifShadow?.hide()
      this.previewMotifHighlight?.hide()
      this.previewMotif?.hide()
    }

    if (hasText) {
      const textAttrs = {
        width: compositionWidth,
        height: textHeight,
        align: this.previewTextAlignment || 'center',
        visible: true,
      }

      this.previewTextShadow?.setAttrs({
        ...textAttrs,
        x: compositionX - effectOffset,
        y: textY - effectOffset,
      })

      this.previewTextHighlight?.setAttrs({
        ...textAttrs,
        x: compositionX + effectOffset,
        y: textY + effectOffset,
      })

      this.previewText.setAttrs({
        ...textAttrs,
        x: compositionX,
        y: textY,
      })
    } else {
      this.previewTextShadow?.hide()
      this.previewTextHighlight?.hide()
      this.previewText.hide()
    }

    this.updatePreviewDebug({
      hasText,
      hasMotif,
      compositionX,
      compositionY,
      compositionWidth,
      compositionHeight,
      textX: compositionX,
      textY,
      textWidth: compositionWidth,
      textHeight,
      motifX,
      motifY: compositionY,
      motifWidth,
      motifHeight,
    })
  }

  updatePreviewDebug ({
    hasText,
    hasMotif,
    compositionX = 0,
    compositionY = 0,
    compositionWidth = 0,
    compositionHeight = 0,
    textX = 0,
    textY = 0,
    textWidth = 0,
    textHeight = 0,
    motifX = 0,
    motifY = 0,
    motifWidth = 0,
    motifHeight = 0,
  }) {
    if (!PREVIEW_DEBUG || !this.previewDebugGroup) return

    const hasComposition = compositionWidth > 0 && compositionHeight > 0

    this.previewDebugComposition?.setAttrs({
      x: compositionX,
      y: compositionY,
      width: compositionWidth,
      height: compositionHeight,
      visible: hasComposition,
    })

    this.previewDebugText?.setAttrs({
      x: textX,
      y: textY,
      width: textWidth,
      height: textHeight,
      visible: Boolean(hasText && textWidth > 0 && textHeight > 0),
    })

    this.previewDebugMotif?.setAttrs({
      x: motifX,
      y: motifY,
      width: motifWidth,
      height: motifHeight,
      visible: Boolean(hasMotif && motifWidth > 0 && motifHeight > 0),
    })

    this.previewDebugAnchor?.show()
    this.previewDebugGroup.moveToTop()
  }

  loadPreviewFont (fontFamily) {
    if (!fontFamily || !document.fonts) return
    if (this.previewFontFamily === fontFamily) return

    this.previewFontFamily = fontFamily

    document.fonts
      .load(`16px "${fontFamily}"`)
      .then(() => {
        if (this.previewFontFamily !== fontFamily) {
          return
        }

        this.updatePreviewCompositionLayout()
        this.previewLayer?.batchDraw()
      })
      .catch((error) => {
        console.warn(
          '[ProductPersonalisation] Failed to load preview font',
          fontFamily,
          error,
        )
      })
  }

  /*
   * Preview motif
   */

  updatePreviewMotif (state) {
    if (!this.previewMotif) return

    const source = state.motif?.dataset?.icon || ''
    const colour = state.colour?.dataset?.colour || '#000000'

    this.previewMotifColour = colour
    this.previewMotifScale = this.getPreviewMotifScale(state)

    if (!source) {
      this.clearPreviewMotif()

      return
    }

    if (this.previewMotifSource === source && this.previewMotifImage) {
      if (this.previewMotifAppliedColour !== colour) {
        this.colourisePreviewMotif(colour)
      }

      this.previewMotifShadow?.show()
      this.previewMotifHighlight?.show()
      this.previewMotif.show()

      return
    }

    if (this.previewMotifSource === source) {
      return
    }

    this.loadPreviewMotif(source)
  }

  getPreviewMotifScale (state) {
    const baseScale = Number.parseFloat(state.motif?.dataset?.baseScale)
    const configuredScale = Number.parseFloat(state.motif?.dataset?.scale)

    if (
      Number.isFinite(this.previewMotifScaleOverride) &&
      Number.isFinite(baseScale) &&
      baseScale > 0
    ) {
      return Math.max(0.01, baseScale * (1 + this.previewMotifScaleOverride))
    }

    if (!Number.isFinite(configuredScale) || configuredScale <= 0) {
      return Number.isFinite(baseScale) && baseScale > 0 ? baseScale : 1
    }

    return configuredScale
  }

  loadPreviewMotif (source) {
    const image = new Image()
    const loadId = (this.previewMotifLoadId || 0) + 1

    this.previewMotifLoadId = loadId
    this.previewMotifSource = source
    this.previewMotifImage = null
    this.previewMotifAppliedColour = null
    this.previewMotifBounds = undefined

    this.previewMotifShadow?.hide()
    this.previewMotifHighlight?.hide()
    this.previewMotif.hide()
    this.previewMotifShadow?.image(null)
    this.previewMotifHighlight?.image(null)
    this.previewMotif.image(null)

    image.decoding = 'async'

    if (!source.startsWith('data:')) {
      image.crossOrigin = 'anonymous'
    }

    image.onload = () => {
      const isCurrentLoad =
        this.previewMotifLoadId === loadId &&
        this.previewMotifSource === source

      if (!isCurrentLoad || !this.previewMotif) {
        return
      }

      this.previewMotifImage = image

      this.colourisePreviewMotif(this.previewMotifColour || '#000000')

      this.previewMotifShadow?.show()
      this.previewMotifHighlight?.show()
      this.previewMotif.show()
      this.updatePreviewCompositionLayout()
      this.previewLayer?.batchDraw()
    }

    image.onerror = () => {
      const isCurrentLoad =
        this.previewMotifLoadId === loadId &&
        this.previewMotifSource === source

      if (!isCurrentLoad) return

      console.warn(
        '[ProductPersonalisation] Failed to load preview motif',
        source,
      )

      this.clearPreviewMotif()
      this.updatePreviewCompositionLayout()
      this.previewLayer?.batchDraw()
    }

    image.src = source
  }

  colourisePreviewMotif (colour) {
    if (!this.previewMotif || !this.previewMotifImage) {
      return
    }

    const image = this.previewMotifImage
    const imageWidth = image.naturalWidth || image.width
    const imageHeight = image.naturalHeight || image.height

    if (!imageWidth || !imageHeight) return

    if (!this.previewMotifCanvas) {
      this.previewMotifCanvas = document.createElement('canvas')
    }

    const sourceCanvas = this.previewMotifCanvas
    const width = Math.max(1, Math.round(imageWidth))
    const height = Math.max(1, Math.round(imageHeight))

    if (sourceCanvas.width !== width) {
      sourceCanvas.width = width
    }

    if (sourceCanvas.height !== height) {
      sourceCanvas.height = height
    }

    const context = sourceCanvas.getContext('2d')

    if (!context) return

    context.clearRect(0, 0, width, height)

    context.globalCompositeOperation = 'source-over'
    context.drawImage(image, 0, 0, width, height)

    context.globalCompositeOperation = 'source-in'
    context.fillStyle = colour
    context.fillRect(0, 0, width, height)

    context.globalCompositeOperation = 'source-over'

    let displayCanvas = sourceCanvas

    if (this.previewMotifBounds === undefined) {
      try {
        this.previewMotifBounds = this.getPreviewMotifVisibleBounds(
          context,
          width,
          height,
        )
      } catch (error) {
        this.previewMotifBounds = null

        console.warn(
          '[ProductPersonalisation] Could not trim motif transparency',
          error,
        )
      }
    }

    const bounds = this.previewMotifBounds

    if (
      bounds &&
      (bounds.x !== 0 ||
        bounds.y !== 0 ||
        bounds.width !== width ||
        bounds.height !== height)
    ) {
      if (!this.previewMotifDisplayCanvas) {
        this.previewMotifDisplayCanvas = document.createElement('canvas')
      }

      const trimmedCanvas = this.previewMotifDisplayCanvas

      if (trimmedCanvas.width !== bounds.width) {
        trimmedCanvas.width = bounds.width
      }

      if (trimmedCanvas.height !== bounds.height) {
        trimmedCanvas.height = bounds.height
      }

      const trimmedContext = trimmedCanvas.getContext('2d')

      if (trimmedContext) {
        trimmedContext.clearRect(0, 0, bounds.width, bounds.height)

        trimmedContext.drawImage(
          sourceCanvas,
          bounds.x,
          bounds.y,
          bounds.width,
          bounds.height,
          0,
          0,
          bounds.width,
          bounds.height,
        )

        displayCanvas = trimmedCanvas
      }
    }

    this.previewMotif.image(displayCanvas)

    this.previewMotifShadowCanvas = this.colourisePreviewMotifEffect(
      displayCanvas,
      this.previewMotifShadowCanvas,
      this.getPreviewStampShadowColour(colour),
    )

    this.previewMotifHighlightCanvas = this.colourisePreviewMotifEffect(
      displayCanvas,
      this.previewMotifHighlightCanvas,
      this.getPreviewStampHighlightColour(colour),
    )

    this.previewMotifShadow?.image(this.previewMotifShadowCanvas)
    this.previewMotifHighlight?.image(this.previewMotifHighlightCanvas)

    this.previewMotifAppliedColour = colour
  }

  colourisePreviewMotifEffect (sourceCanvas, effectCanvas, colour) {
    const canvas = effectCanvas || document.createElement('canvas')

    if (canvas.width !== sourceCanvas.width) {
      canvas.width = sourceCanvas.width
    }

    if (canvas.height !== sourceCanvas.height) {
      canvas.height = sourceCanvas.height
    }

    const context = canvas.getContext('2d')

    if (!context) return canvas

    context.clearRect(0, 0, canvas.width, canvas.height)
    context.globalCompositeOperation = 'source-over'
    context.drawImage(sourceCanvas, 0, 0)
    context.globalCompositeOperation = 'source-in'
    context.fillStyle = colour
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.globalCompositeOperation = 'source-over'

    return canvas
  }

  getPreviewMotifVisibleBounds (context, width, height) {
    const imageData = context.getImageData(0, 0, width, height)
    const pixels = imageData.data

    let minimumX = width
    let minimumY = height
    let maximumX = -1
    let maximumY = -1

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const alphaIndex = (y * width + x) * 4 + 3

        if (pixels[alphaIndex] === 0) {
          continue
        }

        minimumX = Math.min(minimumX, x)
        minimumY = Math.min(minimumY, y)
        maximumX = Math.max(maximumX, x)
        maximumY = Math.max(maximumY, y)
      }
    }

    if (maximumX < minimumX || maximumY < minimumY) {
      return null
    }

    return {
      x: minimumX,
      y: minimumY,
      width: maximumX - minimumX + 1,
      height: maximumY - minimumY + 1,
    }
  }

  clearPreviewMotif () {
    this.previewMotifLoadId = (this.previewMotifLoadId || 0) + 1

    this.previewMotifSource = null
    this.previewMotifImage = null
    this.previewMotifColour = null
    this.previewMotifAppliedColour = null
    this.previewMotifBounds = null
    this.previewMotifScale = 1
    this.previewMotifShadowCanvas = null
    this.previewMotifHighlightCanvas = null

    if (!this.previewMotif) return

    this.previewMotifShadow?.hide()
    this.previewMotifHighlight?.hide()
    this.previewMotif.hide()
    this.previewMotifShadow?.image(null)
    this.previewMotifHighlight?.image(null)
    this.previewMotif.image(null)
  }

  /*
   * Preview resizing
   */

  requestPreviewResize () {
    if (this.previewResizeFrame) {
      cancelAnimationFrame(this.previewResizeFrame)
    }

    this.previewResizeFrame = requestAnimationFrame(() => {
      this.previewResizeFrame = null

      this.resizePreview()
    })
  }

  resizePreview () {
    if (!this.previewStage) return

    const container = this.els.canvas.element
    const width = Math.round(container.clientWidth)
    const height = Math.round(container.clientHeight)

    if (width <= 0 || height <= 0) return

    const widthChanged = this.previewStage.width() !== width
    const heightChanged = this.previewStage.height() !== height

    if (!widthChanged && !heightChanged) {
      return
    }

    this.previewStage.size({
      width,
      height,
    })

    this.updatePreview(this.getPreviewState(this.getState()))
  }

  /*
   * Preview export & upload
   */

  loadPersonalisationPreviewBackgroundImage (source) {
    if (
      this.previewBackgroundSource === source &&
      this.previewBackgroundImage
    ) {
      return Promise.resolve(this.previewBackgroundImage)
    }

    return new Promise((resolve, reject) => {
      const image = new Image()

      image.decoding = 'async'
      image.crossOrigin = 'anonymous'

      image.onload = () => {
        this.previewBackgroundSource = source
        this.previewBackgroundImage = image

        resolve(image)
      }

      image.onerror = () => {
        reject(new Error('Failed to load personalisation background image'))
      }

      image.src = source
    })
  }

  /*
   * Renders a composite preview image (product photo + motif + text) by
   * cloning the already-laid-out live preview group onto a bigger offscreen
   * stage. Cloning (rather than replaying setup/update methods against a
   * second stage) reuses the exact positioning, recolouring and text layout
   * the customer already sees, scaled up proportionally.
   */

  /*
   * Clones the CURRENT live preview group synchronously — must be called
   * at submit time, before add-to-cart resolves and the caller resets the
   * form (which clears the live motif/text state). The clone is detached
   * and scaled independently, so it stays correct even after the live
   * preview has since been reset for the next personalisation.
   */
  snapshotPersonalisationPreviewGroup () {
    if (!window.Konva || !this.previewGroup || !this.previewStage) {
      return null
    }

    const liveSize = Math.min(
      this.previewStage.width(),
      this.previewStage.height(),
    )

    if (!liveSize) return null

    const scale = PERSONALISATION_PREVIEW_EXPORT_SIZE / liveSize
    const clonedGroup = this.previewGroup.clone()
    const currentPosition = this.previewGroup.position()

    clonedGroup.position({
      x: currentPosition.x * scale,
      y: currentPosition.y * scale,
    })
    clonedGroup.scale({ x: scale, y: scale })

    return clonedGroup
  }

  async buildPersonalisationPreviewBlob (clonedGroup) {
    const Konva = window.Konva
    const source = this.config?.productImage

    if (!Konva || !source || !clonedGroup) {
      clonedGroup?.destroy()

      return null
    }

    const backgroundImage =
      await this.loadPersonalisationPreviewBackgroundImage(source)

    const container = document.createElement('div')

    const exportStage = new Konva.Stage({
      container,
      width: PERSONALISATION_PREVIEW_EXPORT_SIZE,
      height: PERSONALISATION_PREVIEW_EXPORT_SIZE,
    })

    try {
      const backgroundLayer = new Konva.Layer({ listening: false })
      const foregroundLayer = new Konva.Layer({ listening: false })

      exportStage.add(backgroundLayer)
      exportStage.add(foregroundLayer)

      backgroundLayer.add(
        new Konva.Rect({
          x: 0,
          y: 0,
          width: PERSONALISATION_PREVIEW_EXPORT_SIZE,
          height: PERSONALISATION_PREVIEW_EXPORT_SIZE,
          fill: '#ffffff',
          listening: false,
        }),
      )

      backgroundLayer.add(
        new Konva.Image({
          image: backgroundImage,
          x: 0,
          y: 0,
          width: PERSONALISATION_PREVIEW_EXPORT_SIZE,
          height: PERSONALISATION_PREVIEW_EXPORT_SIZE,
          listening: false,
        }),
      )

      foregroundLayer.add(clonedGroup)
      exportStage.draw()

      return await new Promise((resolve, reject) => {
        exportStage
          .toCanvas()
          .toBlob(
            (result) =>
              result
                ? resolve(result)
                : reject(new Error('Failed to export preview canvas')),
            'image/jpeg',
            PERSONALISATION_PREVIEW_EXPORT_QUALITY,
          )
      })
    } finally {
      exportStage.destroy()
    }
  }

  /*
   * Generates and uploads the composite preview from a group snapshot taken
   * at submit time (see snapshotPersonalisationPreviewGroup). Returns the
   * resulting CDN URL, or null if there's nothing to personalise or the
   * upload fails — callers must proceed without an image rather than block
   * the cart operation.
   */
  async getPersonalisationPreviewUrl (clonedGroup) {
    if (!clonedGroup) {
      return null
    }

    try {
      const blob = await this.buildPersonalisationPreviewBlob(clonedGroup)

      if (!blob) return null

      const response = await fetch(
        `${PERSONALISATION_PREVIEW_UPLOAD_URL}?shop=${window.theme.permanentDomain}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'image/jpeg' },
          body: blob,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Personalisation preview upload failed (${response.status})`,
        )
      }

      const { url } = await response.json()

      return url || null
    } catch (error) {
      console.warn(
        '[ProductPersonalisation] Failed to generate preview image',
        error,
      )

      return null
    }
  }
}

/*
 * Personalisation debugger
 */

const isPersonalisationDebugMode =
  new URLSearchParams(window.location.search).get('personalisation_debug') ===
  'true'

if (isPersonalisationDebugMode) {
  customElements.whenDefined('product-personalisation').then(async () => {
    const personalisation = document.querySelector('product-personalisation')

    if (!personalisation) return

    const productUrl = new URL(window.location.href)

    productUrl.searchParams.delete('personalisation_debug')

    try {
      const loaded = await personalisation.loadPersonalisation(
        `${productUrl.pathname}${productUrl.search}`,
      )

      if (!loaded) return

      const { ProductPersonalisationDebugger } = await import(
        '@bao/product-personalisation-debugger'
      )

      if (!personalisation.productPersonalisationDebugger) {
        personalisation.productPersonalisationDebugger =
          new ProductPersonalisationDebugger(personalisation)
      }

      window.drawers.activeDrawerKey = 'personalisation'
    } catch (error) {
      console.error(
        '[ProductPersonalisationDebugger] Failed to load personalisation',
        error,
      )
    }
  })
}

if (!customElements.get('product-personalisation')) {
  customElements.define('product-personalisation', ProductPersonalisation)
}
